import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from './utils';

export type PillTone = 'default' | 'dark' | 'gold' | 'emerald';
export type PillSize = 'sm' | 'md' | 'lg';

export interface PillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  tone?: PillTone;
  size?: PillSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const SIZE_CLS: Record<PillSize, string> = {
  sm: 'h-8 px-3 text-[12px] gap-1.5',
  md: 'h-10 px-4 text-[12.5px] gap-2',
  lg: 'h-12 px-4 text-[13px] gap-2',
};

/**
 * Pill — soft, rounded chip-style button.
 * `active` swaps to a dark fill (mirrors the existing `bg-ink-900` selection style).
 */
export const Pill = forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { active, tone = 'default', size = 'md', leadingIcon, trailingIcon, children, className, type = 'button', ...rest },
  ref,
) {
  const baseColors = active
    ? 'is-selected'
    : tone === 'dark'
      ? 'bg-ink-900 text-white border-transparent hover:bg-ink-800'
      : tone === 'gold'
        ? 'gold-gradient text-white border-transparent shadow-gold'
        : tone === 'emerald'
          ? 'border-transparent text-white'
          : 'bg-white/36 text-ink-700 border-white/60 backdrop-blur hover:bg-white/55';

  return (
    <motion.button
      ref={ref}
      type={type}
      whileTap={{ scale: 0.985 }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(rest as any)}
      className={cn(
        'btn-press btn-shine focus-ring rounded-full inline-flex items-center justify-center font-semibold cursor-pointer border transition-colors',
        SIZE_CLS[size],
        baseColors,
        className,
      )}
      style={
        tone === 'emerald' && !active
          ? { background: 'var(--color-emerald)' }
          : undefined
      }
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </motion.button>
  );
});
